import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import cn from 'classnames';
import {toast} from 'react-toastify';

import {useLoadFile} from 'utils/hooks/useLoadFile';

import Book from 'assets/image/library/book.svg';

import MaterialButton from '../article-card/material-button';
import {ICmService} from '../../redux/cm-service/interfaces';

import './styles.scss';

import {
  addReferrals,
  referralsAsyncActions,
  removeReferrals
} from '../../redux/pageAsyncActions';
import {addCmServiceToFavoritesAction, removeCmServiceFromFavoritesAction} from '../../redux/cm-service/cmServiceAsyncActions';
import {useIntl} from 'react-intl';
import {ReactComponent as CheckImg} from '../../assets/image/library/ok-circle.svg';
import {ReactComponent as EmptyHeart} from '../../assets/image/library/empty-heart.svg';
import {ReactComponent as FullHeart} from '../../assets/image/library/full-heart.svg';

type Props = {
  card: ICmService;
  disabledButtons?: boolean;
  showReferralsButton?: boolean;
  onClick: () => void;
  checked?: boolean;
}

export const CmServiceCard = ({
  card,
  disabledButtons = false,
  showReferralsButton,
  onClick,
  checked
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const {
    getFileUrl
  } = useLoadFile();
  const [cardData, setCardData] = useState<ICmService>(card);
  const [isSave, setIsSave] = useState(card.material);
  const [favorite, setFavorite] = useState(card.favorite);
  const intl = useIntl();

  useEffect(() => {
    const newCard = Object.assign({}, card);

    if (!cardData.srvImageUrl && cardData.srvImageId) {
      getFileUrl(cardData.srvImageId, (url) => {
        if (url) {
          newCard.srvImageUrl = url;
          setCardData(newCard);
        }
      });
    }
  }, [card]);

  useEffect(() => {
    setIsSave(card.material);
  }, [card.material]);

  useEffect(() => {
    setFavorite(card.favorite);
  }, [card.favorite]);

  async function handleFavoriteClick(e: React.MouseEvent) {
    e.stopPropagation();
    const next = !favorite;

    setFavorite(next);

    const success = next
      ? await dispatch(addCmServiceToFavoritesAction(card.srvId))
      : await dispatch(removeCmServiceFromFavoritesAction(card.srvId));

    if (!success) {
      setFavorite(!next);

      return;
    }

    if (next) {
      toast.success(intl.formatMessage({
        id: 'gritx.common.card.addedToFavorites',
        defaultMessage: 'Added to Favorites'
      }));
    }
  }

  function renderHeartButton() {
    return (
      <button
        type="button"
        className="cm-service-area__heart-btn"
        onClick={handleFavoriteClick}
        aria-label={favorite
          ? intl.formatMessage({id: 'gritx.common.card.removeFromFavorites', defaultMessage: 'Remove from Favorites'})
          : intl.formatMessage({id: 'gritx.common.card.addToFavorites', defaultMessage: 'Add to Favorites'})}
      >
        {favorite
          ? <FullHeart className="cm-service-area__heart-btn-icon"/>
          : <EmptyHeart className="cm-service-area__heart-btn-icon"/>}
      </button>
    );
  }

  function addToReferrals() {
    setIsSave(!isSave);
    if (!isSave) {
      dispatch(referralsAsyncActions[addReferrals](card.srvId));
    } else {
      dispatch(referralsAsyncActions[removeReferrals]([card.srvId], false));
    }
  }

  function renderCheckedCard() {
    return <div className="cm-service-area">
      <div className="cm-service-area__card-place">
        <button className="cm-service-area__card" onClick={onClick}>
          <div className="cm-service-area__title">
            {cardData.srvName}
          </div>
          <div className="cm-service-area__content">
            <div className="cm-service-area__active-card">
              <CheckImg className="cm-service-area__check-img"/>
            </div>
          </div>
        </button>
      </div>
    </div>;
  }

  function renderBasicCard() {
    return <div className="cm-service-area">
      <div className="cm-service-area__card-place">
        {renderHeartButton()}
        <button className="cm-service-area__card" onClick={onClick}>
          <div className="cm-service-area__title">
            {cardData.srvName}
          </div>
          <div className="cm-service-area__content">
            <div className="cm-service-area__card-picture">
              <img src={cardData.srvImageUrl || Book} role="presentation" alt="" className="cm-service-area__image"/>
            </div>
            <div className="cm-service-area__information">
              <div className="cm-service-area__description">
                {cardData.orgName}
              </div>
              <div className="cm-service-area__category">
                {cardData.typeTreatments}
              </div>
            </div>
          </div>
        </button>
        <div className={cn('cm-service-area__bottom',
          {'cm-service-area__pro-bottom': showReferralsButton},
          {'cm-service-area__disabled-bottom': disabledButtons})}>
          {showReferralsButton && <div className="cm-service-area__add-btn">
            <MaterialButton material={isSave} disabled={disabledButtons} addToMaterials={addToReferrals} title={intl.formatMessage({
              id: 'gritx.cmresource.card.addToReferrals',
              defaultMessage: 'Add to Referrals'
            })}/>
          </div>}
        </div>
      </div>
    </div>;
  }

  if (checked) {
    return renderCheckedCard();
  }

  return renderBasicCard();
};
